import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config/ApiConfig';

const styles = {
    page: {
        minHeight: '100vh',
        backgroundImage: `url('/assets/desktop.png')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif",
        position: 'relative',
    },
    overlay: {
        position: 'absolute',
        inset: 0,
        background: 'linear-gradient(135deg, rgba(15,23,42,0.72) 0%, rgba(30,58,138,0.60) 100%)',
    },
    card: {
        position: 'relative',
        zIndex: 1,
        width: '100%',
        maxWidth: '400px',
        margin: '1rem',
        background: '#ffffff',
        border: '1px solid #e5e7eb',
        borderRadius: '20px',
        boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
        padding: '2.5rem 2rem',
        color: '#1f2937',
    },
    logoWrapper: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginBottom: '2rem',
    },
    logoIcon: {
        width: 56,
        height: 56,
        borderRadius: '14px',
        background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '0.75rem',
        boxShadow: '0 8px 24px rgba(59,130,246,0.45)',
    },
    title: {
        fontSize: '1.4rem',
        fontWeight: 700,
        letterSpacing: '0.03em',
        margin: 0,
        color: '#1f2937',
    },
    subtitle: {
        fontSize: '0.82rem',
        color: '#6b7280',
        marginTop: '0.25rem',
    },
    fieldGroup: {
        marginBottom: '1rem',
    },
    label: {
        display: 'block',
        fontSize: '0.78rem',
        fontWeight: 600,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: '#6b7280',
        marginBottom: '0.4rem',
    },
    inputWrapper: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
    },
    inputIcon: {
        position: 'absolute',
        left: '0.85rem',
        color: '#9ca3af',
        pointerEvents: 'none',
        display: 'flex',
    },
    togglePassword: {
        position: 'absolute',
        right: '0.6rem',
        background: 'none',
        border: 'none',
        color: '#9ca3af',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0.4rem',
        borderRadius: '6px',
        transition: 'color 0.2s, background 0.2s',
    },
    input: {
        width: '100%',
        padding: '0.7rem 0.9rem 0.7rem 2.6rem',
        background: '#f3f4f6',
        border: '1px solid #d1d5db',
        borderRadius: '10px',
        color: '#1f2937',
        fontSize: '0.95rem',
        outline: 'none',
        transition: 'border-color 0.2s, background 0.2s',
    },
    button: {
        width: '100%',
        padding: '0.75rem',
        marginTop: '1.25rem',
        background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
        border: 'none',
        borderRadius: '10px',
        color: '#fff',
        fontSize: '0.95rem',
        fontWeight: 700,
        letterSpacing: '0.04em',
        cursor: 'pointer',
        boxShadow: '0 6px 20px rgba(59,130,246,0.40)',
        transition: 'opacity 0.2s, transform 0.1s',
    },
    error: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        background: '#fee2e2',
        border: '1px solid #fecaca',
        borderRadius: '8px',
        color: '#dc2626',
        fontSize: '0.83rem',
        padding: '0.55rem 0.8rem',
        marginTop: '0.5rem',
    },
    footer: {
        textAlign: 'center',
        marginTop: '1.75rem',
        fontSize: '0.75rem',
        color: '#9ca3af',
    },
    transparenciaLink: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.45rem',
        marginTop: '1.25rem',
        fontSize: '0.85rem',
        fontWeight: 600,
        color: '#198754',
        textDecoration: 'none',
    },
};

function LoginPage() {
    const [login, setLogin] = useState('');
    const [senha, setSenha] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [sessionMessage, setSessionMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    React.useEffect(() => {
        try {
            const justLoggedOut = sessionStorage.getItem('justLoggedOut');
            if (justLoggedOut === 'true') {
                sessionStorage.removeItem('justLoggedOut');
                sessionStorage.removeItem('loginRedirectMessage');
                return;
            }
            const msg = sessionStorage.getItem('loginRedirectMessage');
            if (msg) setSessionMessage(msg);
        } catch (e) {
            // noop
        }
    }, []);

    const handleEmailChange = (event) => setLogin(event.target.value);
    const handlePasswordChange = (event) => setSenha(event.target.value);

    async function handleSubmit(event) {
        event.preventDefault();
        setLoading(true);
        setErrorMessage('');
        try {
            const response = await axios.post(
                API_BASE_URL.LOGIN,
                { login, senha }
            );
            const { token, usuario, relAssociacaoUsuario, sessionInfo } = response.data;
            if (!token || !usuario || !relAssociacaoUsuario) {
                throw new Error('Dados de login inválidos');
            }

            localStorage.setItem('token', token);
            localStorage.setItem('user', JSON.stringify(usuario));
            localStorage.setItem('relAssociacaoUsuario', JSON.stringify(relAssociacaoUsuario));
            localStorage.setItem('login', usuario.login);
            localStorage.setItem('anoBase', new Date().getFullYear().toString());
            sessionStorage.removeItem('loginRedirectMessage');

            if (sessionInfo) {
                sessionStorage.setItem('loginRedirectMessage', sessionInfo);
            }

            navigate('/seleciona_entidade');
        } catch (error) {
            setErrorMessage('Login ou senha incorretos. Tente novamente.');
            console.error('Erro ao buscar itens:', error);
        } finally {
            setLoading(false);
        }
    }

    return (
        <div style={styles.page}>
            <div style={styles.overlay} />
            <div style={styles.card}>
                {/* Logo / Header */}
                <div style={styles.logoWrapper}>
                    <div style={styles.logoIcon}>
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                        </svg>
                    </div>
                    <h1 style={styles.title}>GFINANC</h1>
                    <span style={styles.subtitle}>Sistema de Gestão Financeira</span>
                </div>

                <form onSubmit={handleSubmit} autoComplete="off">
                    {/* Usuário */}
                    <div style={styles.fieldGroup}>
                        <label style={styles.label}>Usuário</label>
                        <div style={styles.inputWrapper}>
                            <span style={styles.inputIcon}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
                                </svg>
                            </span>
                            <input
                                type="text"
                                placeholder="Digite seu usuário"
                                value={login}
                                onChange={handleEmailChange}
                                required
                                style={styles.input}
                                onFocus={e => { e.target.style.borderColor = 'rgba(59,130,246,0.7)'; e.target.style.background = 'rgba(255,255,255,0.13)'; }}
                                onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.18)'; e.target.style.background = 'rgba(255,255,255,0.08)'; }}
                            />
                        </div>
                    </div>

                    {/* Senha */}
                    <div style={styles.fieldGroup}>
                        <label style={styles.label}>Senha</label>
                        <div style={styles.inputWrapper}>
                            <span style={styles.inputIcon}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                </svg>
                            </span>
                            <input
                                type={showPassword ? 'text' : 'password'}
                                placeholder="Digite sua senha"
                                value={senha}
                                onChange={handlePasswordChange}
                                required
                                style={{ ...styles.input, paddingRight: '2.6rem' }}
                                onFocus={e => { e.target.style.borderColor = 'rgba(59,130,246,0.7)'; e.target.style.background = 'rgba(255,255,255,0.13)'; }}
                                onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.18)'; e.target.style.background = 'rgba(255,255,255,0.08)'; }}
                            />
                            <button
                                type="button"
                                style={styles.togglePassword}
                                onClick={() => setShowPassword(!showPassword)}
                                onMouseEnter={e => { e.target.style.color = '#4b5563'; e.target.style.background = 'rgba(0,0,0,0.05)'; }}
                                onMouseLeave={e => { e.target.style.color = '#9ca3af'; e.target.style.background = 'none'; }}
                                tabIndex={-1}
                                aria-label={showPassword ? 'Esconder senha' : 'Mostrar senha'}
                            >
                                {showPassword ? (
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                                        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                                        <line x1="1" y1="1" x2="23" y2="23" />
                                    </svg>
                                ) : (
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                        <circle cx="12" cy="12" r="3" />
                                    </svg>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Sessão encerrada */}
                    {sessionMessage && (
                        <div style={{ background: 'rgba(255,193,7,0.15)', border: '1px solid rgba(255,193,7,0.4)', borderRadius: '8px', padding: '10px 14px', marginBottom: '1rem', color: '#7c190c', fontSize: '0.85rem', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                            <i className="bi bi-exclamation-triangle-fill flex-shrink-0" style={{ marginTop: '2px' }}></i>
                            <span>{sessionMessage}</span>
                        </div>
                    )}
                    {/* Erro */}
                    {errorMessage && (
                        <div style={styles.error}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                            </svg>
                            {errorMessage}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={loading}
                        style={{ ...styles.button, opacity: loading ? 0.7 : 1 }}
                        onMouseEnter={e => { if (!loading) e.target.style.opacity = '0.88'; }}
                        onMouseLeave={e => { e.target.style.opacity = '1'; }}
                    >
                        {loading ? 'Entrando...' : 'Entrar'}
                    </button>
                </form>

                <a href="/monitoramento" style={styles.transparenciaLink}>
                    <i className="bi bi-clipboard-data"></i>
                    Portal de Monitoramento
                </a>

                <p style={styles.footer}>© {new Date().getFullYear()} GFINANC · Todos os direitos reservados</p>
            </div>
        </div>
    );
}

export default LoginPage;